import store from '@/store/index.js';
import * as Sentry from '@sentry/vue';

function isJSON(jsonString) {
  try {
    const o = JSON.parse(jsonString);

    // Handle non-exception-throwing cases:
    // Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
    // but... JSON.parse(null) returns null, and typeof null === "object",
    // so we must check for that, too. Thankfully, null is falsey, so this suffices:
    if (o && typeof o === 'object') {
      return true;
    }
  } catch (error) {
    Sentry.captureException(error);
    return false;
  }
  return false;
}

function JSONParse(jsonString) {
  if (isJSON(jsonString)) return JSON.parse(jsonString);
  else return {};
}

function ArrayParse(jsonString) {
  if (isJSON(jsonString)) return JSON.parse(jsonString);
  else return [];
}

function stringToArray(string) {
  let new_array = [];

  if (typeof string == 'string') {
    new_array = string.split(',');
    for (let i = 0; i < new_array.length; i++) {
      new_array[i] = new_array[i].replace(/^\s+|\s+$/g, '');
    }
    return new_array;
  } else return false;
}

function arrayToString(array) {
  if (Array.isArray(array)) {
    return array.toString(array);
  } else return false;
}

function deepCopy(obj) {
  if (obj === null || typeof obj !== 'object') return obj;

  const copy = obj.constructor();

  for (const attr in obj) {
    if (obj.hasOwnProperty(attr)) {
      copy[attr] = deepCopy(obj[attr]);
    }
  }

  return copy;
}

function stringToDate(string) {
  //"2019-11-13" 형식의 데이터를 Javascript Date로 변환하여 반환

  const new_date = new Date(string);
  new_date.setHours(0);
  new_date.setMinutes(0);
  new_date.setSeconds(0);

  return new_date;
}

function dateToString(date) {
  //날짜를 "2019-11-13"로 변환하여 반환

  let d = new Date(date),
    month = '' + (d.getMonth() + 1),
    day = '' + d.getDate(),
    year = d.getFullYear();

  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;

  return [year, month, day].join('-');
}

function getTimeStamp() {
  const d = new Date();

  const s =
    leadingZeros(d.getFullYear(), 4) +
    '-' +
    leadingZeros(d.getMonth() + 1, 2) +
    '-' +
    leadingZeros(d.getDate(), 2);

  return s;
}

function leadingZeros(n, digits) {
  let zero = '';
  n = n.toString();

  if (n.length < digits) {
    for (let i = 0; i < digits - n.length; i++) zero += '0';
  }

  return zero + n;
}

async function loadJsonData(url) {
  try {
    const response = await axios.get(url);
    const myObject = response.data; // JSON 데이터가 객체로 저장됩니다.
    return myObject;
  } catch (error) {
    console.error('데이터를 불러오는 중 오류 발생:', error);
  }
}

function getFile(filename, filepath, thumb) {
  const path = store.state.backend_host + '/get_file';

  const params = {
    filename: filename,
    filepath: filepath
  };

  if (thumb == true) {
    params.thumb = true;
  }

  return axios.get(path, { params: params, responseType: 'blob' }).then(response => {
    let file_data = {};
    if (
      (navigator.appName == 'Netscape' &&
        navigator.userAgent.toLowerCase().indexOf('trident') != -1) ||
      navigator.userAgent.toLowerCase().indexOf('msie') != -1 ||
      navigator.appVersion.indexOf('Edge') > -1
    ) {
      // ie 또는 edge일 경우 경우
      file_data = response.data;
      file_data.name = filename;
    } else {
      file_data = new File([response.data], filename);
    }
    const new_file = {};
    new_file.link = URL.createObjectURL(file_data);
    new_file.filename = response.data.filename;

    return new_file;
  });
}

function getFileAsData(filename, filepath, thumb) {
  const path = store.state.backend_host + '/get_file';

  const params = {
    filename: filename,
    filepath: filepath
  };
  if (thumb == true) {
    params.thumb = true;
  }

  return axios.get(path, { params: params, responseType: 'blob' }).then(response => {
    let file_data = {};
    if (
      (navigator.appName == 'Netscape' &&
        navigator.userAgent.toLowerCase().indexOf('trident') != -1) ||
      navigator.userAgent.toLowerCase().indexOf('msie') != -1 ||
      navigator.appVersion.indexOf('Edge') > -1
    ) {
      // ie 또는 edge일 경우 경우
      file_data = response.data;
      file_data.name = filename;
    } else {
      file_data = new File([response.data], filename);
    }

    return file_data;
  });
}

function anchorGo(anchor, should_reload = false) {
  if (anchor === null) {
    anchor = '';
  }

  const baseURL = window.location.origin;
  const queryString = window.location.href.split('?')[1];
  let params = new URLSearchParams(queryString);
  let newURL = null;
  if (params.size > 0) {
    if (params.toString().split('%23').length > 1) {
      params = params.toString().split('%23')[0];
    }
    newURL = baseURL + window.location.pathname + '?' + params.toString() + anchor;
  } else {
    newURL = baseURL + window.location.pathname + anchor;
  }

  window.history.pushState({}, '', newURL);

  if (anchor === '' || should_reload) {
    window.location.reload();
  }

  if (anchor && document.querySelector(anchor)) {
    const elem = document.querySelector(anchor);
    if (elem) {
      elem.scrollIntoView({ behavior: 'smooth' });
    }
  }
}

function objectToFormData(obj, formData, parentKey) {
  formData = formData || new FormData();

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];

      const newKey = parentKey ? `${parentKey}[${key}]` : key;

      if (typeof value === 'object' && !Array.isArray(value)) {
        // 중첩된 객체인 경우 재귀 호출
        objectToFormData(value, formData, newKey);
      } else if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
          const arrayKey = `${newKey}[${i}]`;
          formData.append(arrayKey, value[i]);
        }
      } else {
        // 문자열, 숫자 또는 불리언 값을 FormData에 추가
        formData.append(newKey, value);
      }
    }
  }

  return formData;
}

function formattedText(text) {
  // 개행 문자(\n)을 HTML 엔터티로 변환
  if (typeof text === 'string' && text) {
    return text.replace(/\n/g, '<br>');
  } else {
    return text;
  }
}

const nan_table = {
  nan: {
    KOR: '해당 없음',
    ENG: 'Not applicable'
  },
  illegal: {
    KOR: '법적 금지',
    ENG: 'Legal prohibitions'
  },
  non_disclosure: {
    KOR: '비밀 유지 제한',
    ENG: 'Confidentiality constraints'
  },
  incomplete_info: {
    KOR: '정보 미제공 / 완전',
    ENG: 'Information unavailable / incomplete'
  }
};

const no_omission_gri_code = ['2-1', '2-2', '2-3', '2-4', '2-5', '3-1', '3-2'];

function generateRandomString(length) {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let randomString = '';

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    randomString += characters.charAt(randomIndex);
  }

  return randomString;
}

function getParamsFromQueryString() {
  const currentURL = window.location.href;
  const queryString = currentURL.split('?')[1];

  if (queryString) {
    const paramsArray = queryString.split('&');

    const params = {};

    for (let i = 0; i < paramsArray.length; i++) {
      const param = paramsArray[i].split('=');
      const key = decodeURIComponent(param[0]);
      const value = decodeURIComponent(param[1]);
      params[key] = value;
    }

    return params;
  } else {
    return null;
  }
}

function updateParamsFromQueryString(key, value, removeHash = false, reload = false) {
  const baseURL = window.location.origin;
  const queryString = window.location.href.split('?')[1];
  let remove_hash = window.location.hash;
  let params = new URLSearchParams(queryString);
  params.set(key, value);
  if (!value || value === 'null') {
    params.delete(key);
  }

  let newURL = null;
  if (params.size > 0) {
    if (params.toString().split('%23').length > 1) {
      remove_hash = '%23' + remove_hash.split('#')[1];
      params = params.toString().replace(remove_hash, '');
    }
    newURL =
      baseURL +
      window.location.pathname +
      '?' +
      params.toString() +
      (removeHash ? '' : window.location.hash);
  } else {
    newURL = baseURL + window.location.pathname + (removeHash ? '' : window.location.hash);
  }

  if (reload) {
    window.history.pushState({}, '', window.location.pathname);
    window.location.href = newURL;
  }

  window.history.pushState({}, '', newURL);
}

function get_color_name(color) {
  if (color != null && typeof color === 'object') {
    return color.hex8;
  } else return color;
}

function downloadFile(download_file_name, url, blob_download = false) {
  if (blob_download) {
    if (!url || url.trim() === '') {
      window.alert('파일을 업로드 해주세요.');
      return;
    }

    // 1) URL에서 마지막 '.' 뒤로 오는 확장자를 추출
    const extension = url.substring(url.lastIndexOf('.'));

    // 2) 최종 파일 이름(확장자 포함)을 구성
    const finalFileName = download_file_name + extension;
    fetch(url)
      .then(response => {
        if (!response.ok) {
          return response.json().then(errorData => {
            throw new Error(
              `파일을 다운로드할 수 없습니다: 통신이 원활하지 않거나 없는 파일입니다.`
            );
          });
        }
        return response.blob();
      })
      .then(blob => {
        const contentType = blob.type;
        if (contentType === 'application/json') {
          return blob.text().then(text => {
            const errorData = JSON.parse(text);
            throw new Error(
              `파일을 다운로드할 수 없습니다: 통신이 원활하지 않거나 없는 파일입니다.`
            );
          });
        }
        const link = document.createElement('a');
        const blobUrl = window.URL.createObjectURL(blob);
        link.href = blobUrl;
        link.setAttribute('download', finalFileName); // 파일명 지정
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl); // Blob URL 해제
      })
      .catch(error => {
        window.alert(`${error.message}`);
        Sentry.captureException(error);
      });
  } else {
    window.open(url, '_blank');
  }
}

function handleEditAuth(selected_report, key) {
  if (!selected_report) {
    window.alert('보고서 정보가 없습니다.');
    return false;
  }
  let able_to_edit = true;
  if (!selected_report?.is_able_to_edit) {
    window.alert('서비스 결제가 필요합니다');
    able_to_edit = false;
  }

  if (key) {
    const hasAuth = selected_report?.writing_auth_info?.[key];
    if (hasAuth) {
      able_to_edit = true;
    } else {
      window.alert('편집 권한이 없습니다. 결제 업그레이드가 필요합니다.');
      able_to_edit = false;
    }
  }

  return able_to_edit;
}

function sort_indicator_arr(arr) {
  if (arr === undefined) {
    return null;
  }

  return arr.slice().sort((a, b) => {
    if (a === undefined || b === undefined) {
      return;
    }
    const [numA, subNumA] = a.split('-').map(Number);
    const [numB, subNumB] = b.split('-').map(Number);

    if (numA === numB) {
      return subNumA - subNumB;
    } else {
      return numA - numB;
    }
  });
}

function navAnchor(event, anchor) {
  event.preventDefault();
  if (anchor && document.querySelector('#w' + anchor)) {
    const elem = document.querySelector('#w' + anchor);
    if (elem) {
      elem.scrollIntoView({ behavior: 'smooth' });
    }
  }
}

export {
  isJSON,
  JSONParse,
  ArrayParse,
  stringToArray,
  arrayToString,
  deepCopy,
  stringToDate,
  dateToString,
  getTimeStamp,
  loadJsonData,
  getFile,
  getFileAsData,
  anchorGo,
  objectToFormData,
  formattedText,
  nan_table,
  no_omission_gri_code,
  generateRandomString,
  getParamsFromQueryString,
  updateParamsFromQueryString,
  get_color_name,
  downloadFile,
  handleEditAuth,
  sort_indicator_arr,
  navAnchor
};
